// Utilities for displaying and adding questions.
import { Pad } from 'component/basics';
import { CTAButton } from 'component/button';
import { TextField } from 'component/text';
import { useState } from 'react';
import { View } from 'react-native';
import { useDatastore, useModuleMap, useModuleObject } from 'util/datastore';
import { useConfig } from 'util/features';
import { defineModuleMap } from 'util/moduledata';
import { t } from 'util/stdtypes';
export type QuestionAdderConfig = {
    questionAdderWidgets?: any[];
};

export type AddQuestionBoxProps = { onAdded?: (questionKey?: string) => void; onChange?: (text: string) => void };

export function AddQuestionBox({ onAdded, onChange }: AddQuestionBoxProps) {
    const [question, setQuestion] = useState('');
    const [inProgress, setInProgress] = useState(false);
    const datastore = useDatastore();
    const { questionAdderWidgets } = useConfig<QuestionAdderConfig>();

    async function onAdd() {
        setInProgress(true);
        const result = await datastore.callServerAsync('questions', 'add', { newInstance: { name: question } });
        onAdded && onAdded(result.questionKey);
        setInProgress(false);
        setQuestion('');
        onChange && onChange('');
    }

    return (
        <View>
            <TextField
                autoFocus
                multiline={false}
                onChange={(text) => {
                    setQuestion(text);
                    onChange && onChange(text);
                }}
                placeholderLabel={t('Ask a question...')}
                testID='add-question'
                value={question}
            />
            {(question || inProgress) && <Pad />}
            {inProgress ? (
                <CTAButton disabled label={t('Adding...')} />
            ) : question ? (
                <CTAButton label={t('Add Question')} onPress={onAdd} />
            ) : null}
            <Pad size={8} />
            {questionAdderWidgets &&
                questionAdderWidgets.map((Widget, i) => <Widget instanceKey={question} key={i} structureKey={'question'} />)}
        </View>
    );
}

export type Question = {
    key: string;
    question: string;
    text?: string; // FIXME: Remove this once we confirm its not used any more
    from: string;
    time: number;
    commentCount?: number;
};

export type QuestionWithoutKey = Omit<Question, 'key'>;

export const moduleMapQuestion = defineModuleMap<QuestionWithoutKey>('PUBLIC', 'question', null);

export function useGlobalQuestions(): Question[] {
    const questionObjs = useModuleMap(moduleMapQuestion);
    if (!questionObjs) {
        return [];
    }
    const keys = Object.keys(questionObjs);
    const questions = keys.map((key) => ({ key, ...questionObjs[key] }));
    return questions;
}

export function useQuestion(key: string): QuestionWithoutKey | null | undefined {
    const question = useModuleObject(moduleMapQuestion, key);
    return question;
}
